import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  LayoutDashboard, Users, Send, Calendar, BarChart3, UserCog,
  Shield, Search, MessageSquare, ArrowRight, ChevronRight,
  Star, CheckCircle, Play, Monitor, Smartphone, Globe,
  Phone, Mail, MapPin, Network, Target, Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";

const FeatureTour = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: "dashboard",
      icon: LayoutDashboard,
      title: "Your Command Center",
      subtitle: "Dashboard",
      description: "Get a real-time overview of your referrals, pending meetings, unread messages, and recent leads — all in one place. Know exactly what needs your attention the moment you log in.",
      highlights: ["Referral tracking at a glance", "Pending action items", "Recent lead activity", "Quick access to all tools"],
      color: "#0284C7",
      bgColor: "#E8F4FD",
    },
    {
      id: "directory",
      icon: Search,
      title: "10,000+ Verified Providers",
      subtitle: "Provider Directory",
      description: "Search and filter through our nationwide network of healthcare providers. Find the right specialist by name, specialty, or city in seconds.",
      highlights: ["Search by name or specialty", "Filter by major metro markets", "View provider credentials", "One-click meeting scheduling"],
      color: "#059669",
      bgColor: "#ECFDF5",
    },
    {
      id: "meetings",
      icon: Calendar,
      title: "Schedule Lunches & Meetings",
      subtitle: "Meeting Scheduler",
      description: "Book lunches, breakfasts, coffee meetings, and in-services directly with providers. Choose the meeting type, date, time, and purpose — all facilitated by iConcierge.",
      highlights: ["Lunch, breakfast & coffee meetings", "In-service scheduling", "Purpose-driven bookings", "iConcierge facilitation"],
      color: "#7C3AED",
      bgColor: "#F3E8FF",
    },
    {
      id: "referrals",
      icon: Send,
      title: "Manage Patient Referrals",
      subtitle: "Referral Network",
      description: "Send and receive patient referrals through a trusted network. Track referral status, urgency levels, and response times. Build relationships that drive consistent patient flow.",
      highlights: ["Send & receive referrals", "Track status in real-time", "Urgency level indicators", "Referral history & analytics"],
      color: "#DC2626",
      bgColor: "#FEE2E2",
    },
    {
      id: "messages",
      icon: MessageSquare,
      title: "Direct Secure Messaging",
      subtitle: "Messages",
      description: "Communicate directly with other healthcare professionals on the platform. Discuss referrals, coordinate meetings, and build relationships through secure in-app messaging.",
      highlights: ["Real-time conversations", "Secure & private", "Conversation history", "Unread message alerts"],
      color: "#0891B2",
      bgColor: "#E0F7FA",
    },
    {
      id: "analytics",
      icon: BarChart3,
      title: "Track Your Growth",
      subtitle: "Analytics & ROI",
      description: "Monitor your referral performance, conversion rates, and network growth over time. See monthly trends, identify top referral partners, and measure your ROI.",
      highlights: ["Monthly referral trends", "Conversion rate tracking", "Lead pipeline analytics", "ROI insights"],
      color: "#EA580C",
      bgColor: "#FFF7ED",
    },
    {
      id: "profile",
      icon: UserCog,
      title: "Professional Profile",
      subtitle: "Your Profile",
      description: "Manage your professional information, specialty, practice details, and contact information. A complete profile increases your visibility in the directory and builds trust with peers.",
      highlights: ["Specialty & credentials", "Practice information", "Contact & location", "Referral preferences"],
      color: "#4F46E5",
      bgColor: "#EEF2FF",
    },
    {
      id: "admin",
      icon: Shield,
      title: "Concierge Command Center",
      subtitle: "Admin Dashboard",
      description: "For iConcierge administrators — monitor all activity across the platform. View engagement insights, pending meetings, assistance requests, and facilitate connections between providers.",
      highlights: ["Activity feed & notifications", "Meeting purpose analytics", "Assistance queue", "User management"],
      color: "#0F766E",
      bgColor: "#F0FDFA",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" alt="iConcierge Logo" className="h-10 w-auto" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/browse" className="text-slate-600 hover:text-[#0284C7] transition-colors">Browse Providers</Link>
              <Link to="/#pricing" className="text-slate-600 hover:text-[#0284C7] transition-colors">Pricing</Link>
              <Link to="/brochure" className="text-slate-600 hover:text-[#0284C7] transition-colors">Brochure</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate("/login")}>Sign In</Button>
              <Button onClick={() => navigate("/register")} className="bg-[#0284C7] text-white hover:bg-[#0270a8] rounded-full">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero with Avatar Video */}
      <section className="py-12 px-4 bg-gradient-to-b from-[#E8F4FD] to-white overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 bg-white text-[#0284C7] px-4 py-1.5 rounded-full text-sm font-medium mb-6 shadow-sm border border-[#0284C7]/20">
              <Play className="w-4 h-4" />
              Platform Tour
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              See What's Inside
              <br />
              <span className="text-[#0284C7]">iConcierge</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Watch how healthcare professionals — from front desk staff to surgeons — use the platform every day.
            </p>
          </motion.div>

          {/* Avatar Intro Video */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mx-auto mb-8"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-black">
              <video
                controls
                playsInline
                poster="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/g1m2yb74_persona-dr_sarah.png"
                className="w-full aspect-video"
                data-testid="tour-intro-video"
              >
                <source src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/5k2hdkbu_output.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-sm text-slate-400 mt-3">Meet your iConcierge guide</p>
          </motion.div>

          <div className="flex justify-center gap-6">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Monitor className="w-4 h-4" />Desktop
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Smartphone className="w-4 h-4" />Mobile Responsive
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Globe className="w-4 h-4" />Web-Based
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Feature Walkthrough */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Everything You Need to Grow
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Explore each feature of the platform designed to help you build relationships and drive results.
            </p>
          </div>

          {features.map((feature, index) => (
            <FeatureSection key={feature.id} feature={feature} index={index} isReversed={index % 2 === 1} />
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16 px-4 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "10,000+", label: "Verified Providers" },
              { value: "Nationwide", label: "Coverage" },
              { value: "20+", label: "Specialties" },
              { value: "24/7", label: "Platform Access" },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join the network of healthcare professionals who are growing their practice through trusted relationships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate("/register")}
              className="bg-[#0284C7] text-white hover:bg-[#0270a8] rounded-full px-8 text-base"
            >
              Create Your Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/browse")}
              className="rounded-full px-8 text-base"
            >
              Browse Providers First
            </Button>
          </div>

          {/* Contact */}
          <div className="flex flex-wrap justify-center gap-6 mt-12 pt-8 border-t border-slate-200">
            <a href="tel:8882509421" className="flex items-center gap-2 text-slate-500 hover:text-[#0284C7] text-sm">
              <Phone className="w-4 h-4" />(888) 250-9421
            </a>
            <a href="mailto:contact@iconciergehm.com" className="flex items-center gap-2 text-slate-500 hover:text-[#0284C7] text-sm">
              <Mail className="w-4 h-4" />contact@iconciergehm.com
            </a>
            <span className="flex items-center gap-2 text-slate-500 text-sm">
              <MapPin className="w-4 h-4" />Rockwall, TX
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg" alt="Logo" className="h-8 w-auto" />
          </div>
          <p className="text-slate-500 text-sm">&copy; 2025 iConcierge Healthcare Marketing. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Individual Feature Section with scroll animation
const FeatureSection = ({ feature, index, isReversed }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const mockScreens = {
    dashboard: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">iconciergehm.com/dashboard</span>
        </div>
        <div className="p-5">
          <p className="text-lg font-bold text-slate-900 mb-4">Welcome back, Dr. Smith</p>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[{l:"Referrals Sent",v:"12"},{l:"Received",v:"8"},{l:"Pending",v:"3"},{l:"Messages",v:"5"}].map(s=>(
              <div key={s.l} className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-[#0284C7]">{s.v}</div>
                <div className="text-[10px] text-slate-500">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <span className="font-medium">Complete your profile</span> — Add your specialty to appear in the directory
          </div>
        </div>
      </div>
    ),
    directory: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">iconciergehm.com/browse</span>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-4">
            <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400">Search by name or specialty...</div>
            <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400">All Cities</div>
          </div>
          {[{n:"Dr. Sarah Chen",s:"Cardiology",c:"New York, NY"},{n:"Dr. Michael Rodriguez",s:"Orthopedic Surgery",c:"Chicago, IL"},{n:"Dr. Emily Park",s:"Dermatology",c:"Los Angeles, CA"}].map(p=>(
            <div key={p.n} className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-0">
              <div className="w-10 h-10 bg-[#E8F4FD] rounded-full flex items-center justify-center text-[#0284C7] text-sm font-bold">{p.n.split(' ').map(w=>w[0]).join('').slice(0,2)}</div>
              <div className="flex-1"><div className="font-medium text-slate-900 text-sm">{p.n}</div><div className="text-xs text-[#0284C7]">{p.s}</div><div className="text-xs text-slate-400">{p.c}</div></div>
              <button className="text-xs bg-[#0284C7] text-white px-3 py-1.5 rounded-full">Schedule</button>
            </div>
          ))}
        </div>
      </div>
    ),
    meetings: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Schedule a Meeting</span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[{t:"Lunch",em:"fork-knife"},{t:"Breakfast",em:"coffee"},{t:"Coffee",em:"cup"},{t:"In-Service",em:"people"}].map((m,i)=>(
              <div key={m.t} className={`p-3 rounded-xl border-2 text-center cursor-pointer ${i===0?'border-[#0284C7] bg-[#E8F4FD]':'border-slate-200'}`}>
                <Calendar className={`w-5 h-5 mx-auto mb-1 ${i===0?'text-[#0284C7]':'text-slate-400'}`} />
                <span className={`text-xs font-medium ${i===0?'text-[#0284C7]':'text-slate-500'}`}>{m.t}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500 mb-1">Date</div><div className="text-sm font-medium">May 22, 2026</div></div>
            <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500 mb-1">Time</div><div className="text-sm font-medium">12:00 PM</div></div>
          </div>
          <button className="w-full bg-[#0284C7] text-white py-2.5 rounded-full text-sm font-medium">Send Meeting Request</button>
        </div>
      </div>
    ),
    referrals: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Referrals</span>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-4">
            <button className="bg-[#0284C7] text-white text-xs px-3 py-1.5 rounded-full">Received (3)</button>
            <button className="bg-slate-100 text-slate-600 text-xs px-3 py-1.5 rounded-full">Sent (5)</button>
          </div>
          {[{from:"Dr. Chen",patient:"John D.",urgency:"Urgent",status:"pending"},{from:"Dr. Park",patient:"Maria S.",urgency:"Routine",status:"accepted"},{from:"Dr. Kim",patient:"Robert L.",urgency:"Routine",status:"completed"}].map(r=>(
            <div key={r.patient} className="flex items-center gap-3 p-3 border-b border-slate-100 last:border-0">
              <div className={`w-2 h-2 rounded-full ${r.urgency==='Urgent'?'bg-red-500':'bg-emerald-500'}`}></div>
              <div className="flex-1"><div className="text-sm font-medium text-slate-900">{r.patient}</div><div className="text-xs text-slate-500">From {r.from}</div></div>
              <span className={`text-xs px-2 py-1 rounded-full ${r.status==='pending'?'bg-amber-100 text-amber-700':r.status==='accepted'?'bg-blue-100 text-blue-700':'bg-emerald-100 text-emerald-700'}`}>{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    messages: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Messages</span>
        </div>
        <div className="flex h-[260px]">
          <div className="w-1/3 border-r border-slate-100 p-2">
            {[{n:"Dr. Chen",msg:"Thanks for the referral!",unread:true},{n:"Dr. Park",msg:"See you at lunch",unread:false},{n:"Dr. Kim",msg:"Patient update",unread:true}].map(c=>(
              <div key={c.n} className={`p-2 rounded-lg mb-1 cursor-pointer ${c.unread?'bg-[#E8F4FD]':'hover:bg-slate-50'}`}>
                <div className="text-xs font-medium text-slate-900 flex justify-between">{c.n}{c.unread&&<div className="w-2 h-2 bg-[#0284C7] rounded-full"></div>}</div>
                <div className="text-[10px] text-slate-500 truncate">{c.msg}</div>
              </div>
            ))}
          </div>
          <div className="flex-1 flex flex-col p-3">
            <div className="flex-1 space-y-2">
              <div className="flex justify-start"><div className="bg-slate-100 rounded-lg px-3 py-2 text-xs max-w-[80%]">Hi! I wanted to discuss the referral for John D.</div></div>
              <div className="flex justify-end"><div className="bg-[#0284C7] text-white rounded-lg px-3 py-2 text-xs max-w-[80%]">Of course! Happy to discuss. When works?</div></div>
              <div className="flex justify-start"><div className="bg-slate-100 rounded-lg px-3 py-2 text-xs max-w-[80%]">Thanks for the referral! Patient is doing great.</div></div>
            </div>
            <div className="flex gap-2 mt-2"><input className="flex-1 bg-slate-50 rounded-full px-3 py-1.5 text-xs border border-slate-200" placeholder="Type a message..." /><button className="bg-[#0284C7] text-white rounded-full px-3 py-1.5 text-xs">Send</button></div>
          </div>
        </div>
      </div>
    ),
    analytics: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Analytics</span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[{l:"Sent",v:"24"},{l:"Received",v:"18"},{l:"Leads",v:"42"},{l:"Rate",v:"68%"}].map(s=>(
              <div key={s.l} className="text-center"><div className="text-lg font-bold text-slate-900">{s.v}</div><div className="text-[10px] text-slate-500">{s.l}</div></div>
            ))}
          </div>
          <div className="bg-slate-50 rounded-lg p-3 mb-3">
            <div className="text-xs font-medium text-slate-700 mb-2">Monthly Referral Trends</div>
            <div className="flex items-end gap-1 h-16">
              {[30,45,35,60,50,72,65,80,75,90,85,95].map((h,i)=>(
                <div key={`bar-${h}-${i}`} className="flex-1 bg-[#0284C7] rounded-t" style={{height:`${h}%`, opacity: 0.4 + (i/12)*0.6}}></div>
              ))}
            </div>
            <div className="flex justify-between mt-1"><span className="text-[8px] text-slate-400">Jan</span><span className="text-[8px] text-slate-400">Jun</span><span className="text-[8px] text-slate-400">Dec</span></div>
          </div>
        </div>
      </div>
    ),
    profile: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Your Profile</span>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#E8F4FD] rounded-full flex items-center justify-center text-[#0284C7] text-xl font-bold">JS</div>
            <div><div className="font-bold text-slate-900">Dr. Jane Smith</div><div className="text-xs text-slate-500">jane@clinic.com</div><div className="flex gap-1 mt-1"><span className="text-[10px] bg-[#E8F4FD] text-[#0284C7] px-2 py-0.5 rounded-full">Member</span><span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Profile Complete</span></div></div>
          </div>
          <div className="space-y-2">
            {[{l:"Specialty",v:"Cardiology"},{l:"Practice",v:"Heart & Vascular Center"},{l:"City",v:"New York, NY"},{l:"Phone",v:"(212) 555-0100"}].map(f=>(
              <div key={f.l} className="flex justify-between py-1.5 border-b border-slate-100"><span className="text-xs text-slate-500">{f.l}</span><span className="text-xs font-medium text-slate-900">{f.v}</span></div>
            ))}
          </div>
        </div>
      </div>
    ),
    admin: (
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
          <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400"></div><div className="w-3 h-3 rounded-full bg-amber-400"></div><div className="w-3 h-3 rounded-full bg-emerald-400"></div></div>
          <span className="text-xs text-slate-400 ml-2">Concierge Command Center</span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[{l:"Users",v:"156"},{l:"Meetings",v:"42"},{l:"Referrals",v:"89"},{l:"Requests",v:"7"}].map(s=>(
              <div key={s.l} className="bg-slate-50 rounded-lg p-2 text-center"><div className="text-lg font-bold text-slate-900">{s.v}</div><div className="text-[10px] text-slate-500">{s.l}</div></div>
            ))}
          </div>
          <div className="text-xs font-medium text-slate-700 mb-2">Activity Feed</div>
          {[{t:"New Lunch Request",d:"Joshua from Hanger Clinics"},{t:"Provider Submitted",d:"Dr. Unknown Provider — Rockwall"},{t:"Contact Form",d:"Client Test sent a message"}].map(n=>(
            <div key={n.t} className="flex items-center gap-2 p-2 border-b border-slate-100 last:border-0">
              <div className="w-2 h-2 bg-[#0284C7] rounded-full flex-shrink-0"></div>
              <div><div className="text-xs font-medium text-slate-900">{n.t}</div><div className="text-[10px] text-slate-500">{n.d}</div></div>
            </div>
          ))}
        </div>
      </div>
    ),
  };

  return (
    <div ref={ref} className="mb-24 last:mb-0">
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
        className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12`}
      >
        {/* Text */}
        <div className="flex-1 max-w-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: feature.bgColor }}>
              <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: feature.color }}>{feature.subtitle}</div>
              <h3 className="text-2xl font-bold text-slate-900">{feature.title}</h3>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed mb-6">{feature.description}</p>
          <ul className="space-y-2">
            {feature.highlights.map((h) => (
              <li key={h} className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: feature.color }} />
                {h}
              </li>
            ))}
          </ul>
        </div>

        {/* Mock Screen */}
        <div className="flex-1 max-w-md w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: isReversed ? -30 : 30 }}
            animate={isInView ? { opacity: 1, scale: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {mockScreens[feature.id]}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default FeatureTour;
